import React from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox, Flex, Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { UserRes } from 'api/swagger';
import { useRoleGuard } from 'shared/hooks/use-role-guard';
import { useTransformToTableData } from 'shared/hooks/use-transform-to-table-data';
import { UserRole } from 'types/user';
import { UserEdit } from 'components/users/user-edit';
import type { IUsersListProps } from './types';
import styles from './users-list.module.scss';

export const UsersList: React.FC<IUsersListProps> = (props) => {
  const { users, isLoading } = props;
  const { t } = useTranslation();
  const isAdmin = useRoleGuard([UserRole.admin]);

  const tableData = useTransformToTableData(users);

  const columns: ColumnsType<UserRes> = [
    {
      title: t('common:name'),
      dataIndex: 'name',
      width: '20%',
      render: (name: string) => name,
    },
    {
      title: t('common:email'),
      dataIndex: 'email',
      render: (email: string) => email,
    },
    {
      title: t('common:role'),
      key: 'role',
      dataIndex: 'role',
      render: (role: UserRole) => t(`user:${role}`),
    },
    ...(isAdmin
      ? [
          {
            title: t('user:bonuses'),
            dataIndex: 'bonusLimit',
            width: '10%',
            render(_: unknown, record: UserRes) {
              return record.bonusPercent ? `${record.bonusPercent}% (over €${record.bonusLimit})` : '-';
            },
          },
        ]
      : []),
    {
      title: 'Is blocked',
      dataIndex: 'isBlocked',
      render: (isBlocked: boolean) => <Checkbox checked={isBlocked} disabled />,
    },
    {
      title: t('common:phone'),
      width: '18%',
      render(_: unknown, record: UserRes) {
        return (
          <Flex vertical>
            <div>{record.phone ?? '-'}</div>
            <div className={styles.phoneDescription}>{record.phoneDescription ?? ''}</div>
          </Flex>
        );
      },
    },
    ...(isAdmin
      ? [
          {
            title: 'Actions',
            dataIndex: 'actions',
            render: (_: unknown, record: UserRes) => <UserEdit user={record} />,
          },
        ]
      : []),
  ];

  return (
    <div>
      <Table
        rowClassName={(record) => (record.isBlocked ? styles.canceledRow : '')}
        size={'small'}
        columns={columns}
        dataSource={tableData}
        pagination={false}
        loading={isLoading ? { size: 'large', delay: 200 } : false}
        bordered
      />
    </div>
  );
};
